/* eslint-disable max-lines */
import React, { useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { MaybeTooltip } from '@modernloop/shared/components';
import { PublicError } from '@modernloop/shared/helper-components';
import { EmailIcon, UnknownUserIcon } from '@modernloop/shared/icons';
import {
  Autocomplete,
  AutocompleteGetTagProps,
  AutocompleteInputChangeReason,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  ListItem,
  Stack,
  TextField,
  inputBaseClasses,
  outlinedInputClasses,
} from '@mui/material';
import { uniqBy } from 'lodash';
import { useDebouncedCallback } from 'use-debounce';

import { EmployeePicker_EmployeeFragment, useEmployeePickerListQuery } from 'src/generated/mloop-graphql';

import { useOrgAtsService } from 'src/hooks/atsService';
import { DirectoryEmployeeIntegrationTypes } from 'src/hooks/onboarding/types';

import { DEBOUNCE_TIMEOUT, EMAIL_REGEX, PAGE_SIZE } from 'src/constants';

// Component-local fragments and query for codegen + runtime usage
export const EmployeePickerList = gql`
  fragment EmployeePicker_employee on Employee {
    id
    fullName
    email
    orgId
    internalProfilePictureUrl
    timezone
    atsId
    isDirectoryDisabled
    isArchived
    isAtsDisabled
    hasAtsId
    title
  }
  query EmployeePickerList($input: EmployeeSearchInput!) {
    employeeSearch(input: $input) {
      total
      items {
        ...EmployeePicker_employee
      }
    }
  }
`;

const DUMMY_LOADING_EMPLOYEE_ID = 'DUMMY_LOADING_EMPLOYEE_ID';

export type Value<
  TData extends EmployeePicker_EmployeeFragment,
  Freeform extends boolean | undefined,
> = Freeform extends false | undefined ? TData : TData | string;

export type Values<TData extends EmployeePicker_EmployeeFragment, Freeform extends boolean | undefined> = Value<
  TData,
  Freeform
>[];

export const renderTagDefault = <TData extends EmployeePicker_EmployeeFragment, Freeform extends boolean | undefined>(
  value: Value<TData, Freeform>,
  index: number,
  getTagProps: AutocompleteGetTagProps
) => {
  const isInvalidEmail =
    typeof value === 'string'
      ? value.match(EMAIL_REGEX) === null
      : value.isArchived || value.isAtsDisabled || !value.hasAtsId || value.isDirectoryDisabled;

  return (
    <Chip
      label={typeof value === 'string' ? value : (value.fullName ?? '')}
      sx={
        isInvalidEmail
          ? {
              border: (theme) => `1px solid ${theme.palette.error.main} !important`,
              backgroundColor: (theme) => `${theme.palette.error.tint} !important`,
            }
          : undefined
      }
      avatar={typeof value === 'string' ? <EmailIcon /> : <Avatar src={value.internalProfilePictureUrl ?? undefined} />}
      {...getTagProps({ index })}
    />
  );
};

type BaseEmployeePickerProps<TData extends EmployeePicker_EmployeeFragment, Freeform extends boolean | undefined> = {
  dataTestId?: string;
  employees: Values<TData, Freeform>;
  loading: boolean;
  freeformEnabled?: boolean;
  label?: string;
  error?: boolean;
  selectedEmployees?: Values<TData, Freeform>;
  defaultSelectedEmployees?: Values<TData, Freeform>;
  additionalEmployeeOptions?: Values<TData, Freeform>;

  // Allows selecting employees who are not in ATS.
  // E.g. On Microsoft orgs we allow selecting employees who are not in ATS for storing it in arr_outlook_shared_calendar_emails pref
  allowSelectingAllEmployees?: boolean;

  onChange: (EmployeeFragment: Values<TData, Freeform>) => void;
  autoFocus?: boolean;
  placeholder?: string;
  onClose?: () => void;
  renderTags?: (value: Values<TData, Freeform>, getTagProps: AutocompleteGetTagProps) => React.ReactNode;
  open?: boolean;
  filterOptions?: (employees: Values<TData, Freeform>) => Values<TData, Freeform>;
  onInputChange?: (event: React.ChangeEvent, value: string, reason: AutocompleteInputChangeReason) => void;
  getOptionDisabled?: (option: Freeform extends false | undefined ? TData : TData | string) => boolean;
  disabled?: boolean;
  getOptionSelected?: (option: Value<TData, Freeform>, value: Value<TData, Freeform>) => boolean;
};

export const BaseEmployeePicker = <
  TData extends EmployeePicker_EmployeeFragment,
  Freeform extends boolean | undefined,
>({
  dataTestId,
  loading,
  freeformEnabled,
  label,
  error,
  employees,
  selectedEmployees,
  defaultSelectedEmployees,
  additionalEmployeeOptions,
  allowSelectingAllEmployees,
  open,
  autoFocus,
  placeholder,
  onChange,
  onClose,
  renderTags,
  filterOptions,
  onInputChange,
  getOptionDisabled,
  disabled,
  getOptionSelected,
}: BaseEmployeePickerProps<TData, Freeform>): JSX.Element => {
  const ats = useOrgAtsService();
  const { t } = useTranslation();

  const employeesOptions = useMemo((): Values<TData, Freeform> => {
    if (filterOptions && employees.length) {
      return filterOptions(
        uniqBy([...employees, ...(selectedEmployees || []), ...(additionalEmployeeOptions || [])], (emp) =>
          typeof emp === 'string' ? emp : emp.id
        )
      );
    }

    /**
     * The below sort tries to push any user who satisfies the below conditions to the bottom of list:
     * - Is archived in ModernLoop.
     * - Has not been invited to ATS.
     * - Has been invited to ATS but has not accepted the invite and created an account.
     */
    return uniqBy([...employees, ...(selectedEmployees || []), ...(additionalEmployeeOptions || [])], (emp) =>
      typeof emp === 'string' ? emp : emp.id
    ).sort((a, b) => {
      if (typeof a === 'string' && typeof b === 'string') {
        return a.localeCompare(b);
      }
      if (typeof a === 'string') {
        return 1;
      }
      if (typeof b === 'string') {
        return -1;
      }
      return (
        Number(Boolean(a.isArchived || a.isAtsDisabled || !a.hasAtsId)) -
        Number(Boolean(b.isArchived || b.isAtsDisabled || !b.hasAtsId))
      );
    });
  }, [employees, filterOptions, additionalEmployeeOptions, selectedEmployees]);

  const handleChange = (_event: React.ChangeEvent, value: Values<TData, Freeform>) => {
    const freeformEmails = value.filter((val) => typeof val === 'string') as string[];
    const hasInvalidEmail = freeformEmails.reduce((prev, data) => {
      if (prev) {
        return prev;
      }
      return data.match(EMAIL_REGEX) === null;
    }, false);

    if (hasInvalidEmail) {
      return;
    }

    onChange(value);
  };

  const isOptionEqualToValue = (option: Value<TData, Freeform>, value: Value<TData, Freeform>) => {
    if (getOptionSelected) {
      return getOptionSelected(option, value);
    }

    const optionId = typeof option === 'string' ? option : option.id;
    const valueId = typeof value === 'string' ? value : value.id;
    return optionId === valueId;
  };

  const renderTagsDefault = (values: Values<TData, Freeform>, getTagProps: AutocompleteGetTagProps) => {
    return values.map((value, index) => renderTagDefault(value, index, getTagProps));
  };

  const getLoadingIcon = (): JSX.Element => {
    return (
      <Stack direction="row" justifyContent="center">
        <CircularProgress size={24} />
      </Stack>
    );
  };

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: Value<TData, Freeform>) => {
    // We don't render freeform in options.
    if (typeof option === 'string') {
      return null;
    }

    if (option.id === DUMMY_LOADING_EMPLOYEE_ID) {
      return getLoadingIcon();
    }

    let disabledLabel: string | JSX.Element = '';
    const atsName = ats?.name || 'ATS';
    if (option.isAtsDisabled) {
      disabledLabel = t('hiring-dashboard-common:common.employeePicker.disabledInAts', { atsName });
    } else if (!option.hasAtsId) {
      disabledLabel = t('hiring-dashboard-common:common.employeePicker.notAcceptedInvite', { atsName });
    } else if (option.isArchived) {
      disabledLabel = t('hiring-dashboard-common:common.employeePicker.disabledInModernLoop');
    }

    return (
      <ListItem
        {...props}
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
        }}
      >
        <Box>
          {option.id && <Avatar alt={`${option.fullName}-option`} src={option.internalProfilePictureUrl ?? ''} />}
          {!option.id && <UnknownUserIcon />}
        </Box>
        <Box flexShrink={0}>{option.fullName}</Box>
        {!allowSelectingAllEmployees && disabledLabel && (
          <Box
            minWidth={0}
            onClick={(e) => {
              // This prevents a disabled user from being clicked
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <MaybeTooltip
              label={disabledLabel}
              title={disabledLabel}
              labelProps={{
                variant: 'body2',
              }}
            />
          </Box>
        )}
      </ListItem>
    );
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const additionalProps: { open?: boolean } = {};
  if (open !== undefined) {
    additionalProps.open = open;
  }

  return (
    <Autocomplete<Value<TData, Freeform>, true, true, Freeform>
      loading={loading}
      disabled={disabled}
      autoHighlight
      fullWidth
      multiple
      disableClearable
      filterSelectedOptions
      freeSolo={freeformEnabled as Freeform}
      popupIcon={null}
      options={loading ? [{ id: DUMMY_LOADING_EMPLOYEE_ID } as TData, ...employeesOptions] : employeesOptions}
      renderInput={(params) => (
        <Box>
          <TextField
            {...params}
            data-testid={dataTestId}
            error={error}
            label={label}
            autoFocus={autoFocus}
            placeholder={placeholder}
            sx={
              label
                ? {
                    [`& .${outlinedInputClasses.root}.${inputBaseClasses.sizeSmall}`]: {
                      paddingTop: 2.5,
                    },
                  }
                : undefined
            }
          />
        </Box>
      )}
      value={selectedEmployees}
      defaultValue={defaultSelectedEmployees}
      getOptionLabel={(option) => (typeof option === 'string' ? option : (option.fullName ?? ''))}
      renderOption={(props, option) => renderOption(props, option)}
      renderTags={renderTags || renderTagsDefault}
      onChange={handleChange}
      isOptionEqualToValue={isOptionEqualToValue}
      onClose={handleClose}
      getOptionDisabled={(option) => {
        if (
          typeof option !== 'string' &&
          (option.id === DUMMY_LOADING_EMPLOYEE_ID ||
            option.isAtsDisabled ||
            (!allowSelectingAllEmployees && !option.hasAtsId) ||
            option.isArchived)
        ) {
          return true;
        }
        if (getOptionDisabled) {
          return getOptionDisabled(option);
        }
        return false;
      }}
      onInputChange={onInputChange}
      loadingText={getLoadingIcon()}
      {...additionalProps}
    />
  );
};

export type EmployeePickerProps<TData extends EmployeePicker_EmployeeFragment, Freeform extends boolean | undefined> = {
  dataTestId?: string;
  autoFocus?: boolean;
  freeformEnabled?: Freeform;
  label?: string;
  error?: boolean;
  placeholder?: string;
  selectedEmployees?: Values<TData, Freeform>;
  defaultSelectedEmployees?: Values<TData, Freeform>;
  additionalEmployeeOptions?: Values<TData, Freeform>;
  allowSelectingAllEmployees?: boolean;
  onChange: (EmployeeFragment: Values<TData, Freeform>) => void;
  onClose?: () => void;
  renderTags?: (value: Values<TData, Freeform>, getTagProps: AutocompleteGetTagProps) => React.ReactNode;
  open?: boolean;
  filterOptions?: (employees: Values<TData, Freeform>) => Values<TData, Freeform>;
  requestWithATSid?: boolean;
  disabled?: boolean;
  onInputChange?: (event: React.ChangeEvent, value: string, reason: AutocompleteInputChangeReason) => void;
  getOptionSelected?: (option: Value<TData, Freeform>, value: Value<TData, Freeform>) => boolean;
  showUnassignedOption?: boolean;
  hasEmployeeDirectoryIntegrations?: boolean;
};

const EmployeePicker = <
  TData extends EmployeePicker_EmployeeFragment,
  Freeform extends boolean | undefined = undefined,
>(
  props: EmployeePickerProps<TData, Freeform>
): JSX.Element => {
  const { requestWithATSid, showUnassignedOption, hasEmployeeDirectoryIntegrations } = props;
  const { t } = useTranslation();

  const [searchStr, setSearchStr] = useState<string>('');

  const { loading, data, error } = useEmployeePickerListQuery({
    variables: {
      input: {
        pageInput: {
          limit: PAGE_SIZE,
        },
        isArchived: !searchStr ? false : undefined,
        isAtsDisabled: !searchStr ? false : undefined,
        isDirectoryDisabled: false,
        hasAtsId: requestWithATSid !== undefined ? requestWithATSid : undefined,
        search: searchStr,
        hasIntegrations: hasEmployeeDirectoryIntegrations ? DirectoryEmployeeIntegrationTypes : undefined,
      },
    },
  });

  const handleSearch = (value: string) => {
    if (data?.employeeSearch?.items && data.employeeSearch.items.length === data.employeeSearch.total && !searchStr) {
      return;
    }

    setSearchStr(value.toLowerCase());
  };

  const debouncedHandleSearch = useDebouncedCallback(handleSearch, DEBOUNCE_TIMEOUT);

  const handleInputChange = (event: React.ChangeEvent, value: string, reason: AutocompleteInputChangeReason) => {
    const { onInputChange } = props;
    if (onInputChange) {
      onInputChange(event, value, reason);
    }
    debouncedHandleSearch(value);
  };

  const employees = useMemo(() => {
    let employeeList = data?.employeeSearch?.items ?? [];

    if (showUnassignedOption) {
      employeeList = [
        {
          id: null,
          fullName: t('hiring-dashboard-candidates:candidates.hiringTeam.unassigned'),
          hasAtsId: true,
          email: '',
          isDirectoryDisabled: false,
          orgId: '',
          isAtsDisabled: false,
        } as EmployeePicker_EmployeeFragment,
        ...employeeList,
      ];
    }

    return employeeList;
  }, [data?.employeeSearch?.items, showUnassignedOption, t]);

  if (error) {
    return <PublicError error={error} />;
  }

  return <BaseEmployeePicker {...props} onInputChange={handleInputChange} employees={employees} loading={loading} />;
};

export default EmployeePicker;
